import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  PERIODIC_CODE_TO_ANNEXURE_ID,
  type OngoingVendorReviewAnnexureCode,
} from './ongoingVendorReviewApi';

/** Legacy base path (bookmarks); new UI lives at {@link ONGOING_VENDOR_REVIEWS_PATH}. */
export const ONGOING_ANNEXURES_BASE = '/vms/ongoing/annexures';

/** Primary ongoing vendor assessment hub (replaces `/vms/ongoing2`). */
export const ONGOING_VENDOR_REVIEWS_PATH = '/vms/ongoing';

export const ONGOING_VENDOR_REVIEW_LIST_HUBS = ['performance', 'periodic'] as const;
export type OngoingVendorReviewListHub = (typeof ONGOING_VENDOR_REVIEW_LIST_HUBS)[number];

export function ongoingVendorReviewListHubHref(hub: OngoingVendorReviewListHub): string {
  return `${ONGOING_VENDOR_REVIEWS_PATH}?hub=${hub}`;
}

export const ONGOING_VENDOR_PERFORMANCE_HUB_HREF = ongoingVendorReviewListHubHref('performance');
export const ONGOING_VENDOR_PERIODIC_HUB_HREF = ongoingVendorReviewListHubHref('periodic');

export function parseOngoingVendorReviewListHub(
  searchParams: URLSearchParams,
): OngoingVendorReviewListHub | null {
  const raw = searchParams.get('hub')?.trim().toLowerCase();
  if (raw === 'performance' || raw === 'periodic') return raw;
  return null;
}

export const ONGOING_ASSESSMENT_SEGMENTS = ['monthly', 'periodic', 'exit'] as const;
export type OngoingAssessmentSegment = (typeof ONGOING_ASSESSMENT_SEGMENTS)[number];

export function isOngoingAssessmentSegment(s: string | undefined): s is OngoingAssessmentSegment {
  return s != null && (ONGOING_ASSESSMENT_SEGMENTS as readonly string[]).includes(s);
}

/** Map legacy `annexure=6` style query values to new periodic keys (`a6`). */
export function mapLegacyOngoingAnnexureQueryParam(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const t = raw.trim().toLowerCase();
  if (t === 'a6' || t === 'a7' || t === 'a8' || t === 'a10' || t === 'a12' || t === 'a13') return t;
  const digitMap: Record<string, string> = {
    '6': 'a6',
    '7': 'a7',
    '8': 'a8',
    '10': 'a10',
    '12': 'a12',
    '13': 'a13',
  };
  return digitMap[t] ?? null;
}

/**
 * Deep links for ongoing assessments (monthly / periodic) on the new hub.
 * Exit planning continues to use `/vms/vendor-exit`.
 */
/** Annexure 11 → monthly; all other ongoing batch annexures → periodic. */
export function assessmentTypeForOngoingAnnexureCode(code: string): Exclude<OngoingAssessmentSegment, 'exit'> {
  return String(code ?? '').trim() === '11' ? 'monthly' : 'periodic';
}

/** Deep link from My Tray / pending lists into onboarding annexure fill for a specific code. */
export function onboardingAnnexureApprovalHref(args: {
  annexureCode: string;
  vmsRegisteredVendorId: number | null | undefined;
}): string {
  const regId = Number(args.vmsRegisteredVendorId);
  if (!Number.isInteger(regId) || regId <= 0) {
    return '/vms/onboarding/annexures';
  }
  const sp = new URLSearchParams();
  sp.set('vms_registered_vendor_id', String(regId));
  const code = String(args.annexureCode ?? '').trim();
  if (code) sp.set('annexure', code);
  return `/vms/onboarding/annexures?${sp.toString()}`;
}

/** Deep link from My Tray / pending lists into the correct ongoing assessment + annexure form. */
export function ongoingAnnexureApprovalHref(args: {
  annexureCode: string;
  vmsOutsourcingVendorId?: number | null;
  vmsRegisteredVendorId?: number | null;
  ongoingBatchId?: number | null;
}): string {
  const code = String(args.annexureCode ?? '').trim();
  const assessment = assessmentTypeForOngoingAnnexureCode(code);
  const sp = new URLSearchParams();
  const outId = Number(args.vmsOutsourcingVendorId);
  const regId = Number(args.vmsRegisteredVendorId);
  if (Number.isInteger(outId) && outId > 0) {
    sp.set('vms_outsourcing_vendor_id', String(outId));
  } else if (Number.isInteger(regId) && regId > 0) {
    sp.set('vms_registered_vendor_id', String(regId));
  }
  const batchId = Number(args.ongoingBatchId);
  if (Number.isInteger(batchId) && batchId > 0) {
    sp.set('ongoing_batch_id', String(batchId));
  }
  if (assessment === 'periodic') {
    const annexureId = PERIODIC_CODE_TO_ANNEXURE_ID[code as OngoingVendorReviewAnnexureCode];
    if (annexureId) sp.set('annexure', annexureId);
  }
  const q = sp.toString();
  return ongoingAnnexuresPath(assessment, q ? `?${q}` : undefined);
}

export function ongoingAnnexuresPath(segment: OngoingAssessmentSegment, search?: string): string {
  const rawSearch = search ?? '';
  const vendorSuffix = ongoingAnnexuresVendorQuerySuffix(rawSearch);
  if (segment === 'exit') {
    return `/vms/vendor-exit${vendorSuffix}`;
  }
  const sp = new URLSearchParams(vendorSuffix.startsWith('?') ? vendorSuffix.slice(1) : '');
  sp.set('assessment', segment);
  const oldSp = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  const batch = oldSp.get('ongoing_batch_id');
  if (batch && /^\d+$/.test(batch)) sp.set('ongoing_batch_id', batch);
  const annex = oldSp.get('annexure');
  const mapped = mapLegacyOngoingAnnexureQueryParam(annex);
  if (mapped) sp.set('annexure', mapped);
  const q = sp.toString();
  return q ? `${ONGOING_VENDOR_REVIEWS_PATH}?${q}` : `${ONGOING_VENDOR_REVIEWS_PATH}?assessment=${segment}`;
}

/**
 * Query string for ongoing sidebar links: keep vendor context from the current URL, drop
 * `ongoing_batch_id` so switching monthly ↔ periodic does not carry a batch from the other type.
 */
export function ongoingAnnexuresVendorQuerySuffix(locationSearch: string): string {
  const raw = locationSearch.startsWith('?') ? locationSearch.slice(1) : locationSearch;
  const sp = new URLSearchParams(raw);
  const next = new URLSearchParams();
  const reg = sp.get('vms_registered_vendor_id');
  const out = sp.get('vms_outsourcing_vendor_id');
  if (reg && /^\d+$/.test(reg)) next.set('vms_registered_vendor_id', reg);
  if (out && /^\d+$/.test(out)) next.set('vms_outsourcing_vendor_id', out);
  const q = next.toString();
  return q ? `?${q}` : '';
}

/** Old `/vms/ongoing/annexures?…` → `/vms/ongoing?assessment=monthly&…` */
export function OngoingAnnexuresLegacyRedirect() {
  const { search } = useLocation();
  return <Navigate to={ongoingAnnexuresPath('monthly', search)} replace />;
}

/** Old `/vms/ongoing/annexures/:segment?…` → `/vms/ongoing?…` or vendor exit. */
export function OngoingAnnexuresSegmentLegacyRedirect() {
  const { assessmentType } = useParams();
  const { search } = useLocation();
  if (!isOngoingAssessmentSegment(assessmentType)) {
    return <Navigate to={`${ONGOING_VENDOR_REVIEWS_PATH}${ongoingAnnexuresVendorQuerySuffix(search)}`} replace />;
  }
  return <Navigate to={ongoingAnnexuresPath(assessmentType, search)} replace />;
}

/** `/vms/ongoing2` bookmarks → `/vms/ongoing` preserving query string. */
export function OngoingVendorReviewsLegacyTabRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${ONGOING_VENDOR_REVIEWS_PATH}${search}`} replace />;
}
